export {};

// Types for Storefront GraphQL product responses
interface DCFImageNode {
  url: string;
  altText: string | null;
  id?: string;
}

interface DCFImagesConnection {
  edges: { node: DCFImageNode }[];
}

interface DCFSelectedVariant {
  id: string;
  image: { url: string; altText: string | null } | null;
  price: { amount: string; currencyCode: string };
  selectedOptions: { name: string; value: string }[];
}

interface DCFPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface DCFProduct {
  id: string;
  handle: string;
  title: string;
  availableForSale: boolean;
  vendor: string;
  productType: string;
  createdAt: string;
  tags: string[];
  metafield?: { value: string } | null;
  featuredImage: DCFImageNode | null;
  priceRange: DCFPriceRange;
  selectedOrFirstAvailableVariant?: DCFSelectedVariant | null;
  variantYes?: DCFSelectedVariant | null;
  variantNo?: DCFSelectedVariant | null;
  images?: DCFImagesConnection;
}

interface DCFProductsConnection {
  pageInfo: { hasNextPage: boolean; endCursor: string };
  edges: { node: DCFProduct }[];
}

interface DCFGraphQLResponse {
  data?: {
    collection?: {
      products?: DCFProductsConnection;
    };
  };
}

// Configuration
const dcfSelectors = {
  mainContent: '#MainContent',
  sections: '[data-product-section]',
  collectionHandle: '[data-collection-handle]',
  loadMoreButton: '[data-load-more]',
  filters: {
    phoneDevice: '#phone-device'
  }
};

const dcfState = {
  currentMarket: (document.querySelector('body[data-country]') as HTMLElement | null)?.dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US',
  collectionHandle: (document.querySelector('[data-collection-handle]') as HTMLElement | null)?.dataset.collectionHandle || '',
  currentLanguage: document.documentElement.lang.toUpperCase(),
  shopUrl: "https://kaseme-dev-page.myshopify.com",
  accessToken: "a815422836370e8d05bd0c6a813dbaa5"
};

const currentCollectionProductHandles = new Set<string>();

// Utilitaires
class CookieManager {
  static setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
  }

  static getCookie(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  }
}

// Détection du navigateur
function isSafari(version: number): boolean {
  const userAgent = navigator.userAgent;
  return userAgent.indexOf("Safari") !== -1 &&
         userAgent.indexOf("Chrome") === -1 &&
         parseFloat(userAgent.split("Version/")[1]) <= version;
}

// Fonctions de construction des requêtes GraphQL
function buildQuery(first: number | string = 12, cursor: string | null = null, filters: string | null = null, collectionHandle: string | null = null, queryAddon = '', sortKey = 'COLLECTION_DEFAULT'): string {
  const handle = collectionHandle || dcfState.collectionHandle;
  const rawFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
  let favPhone = 'iPhone 17';
  try { favPhone = decodeURIComponent(rawFavPhone) || 'iPhone 17'; } catch { favPhone = rawFavPhone; }
  return `
    query FilteredProducts @inContext(language: ${dcfState.currentLanguage}, country: ${dcfState.currentMarket}) {
      collection(handle: "${handle}") {
        products(first: ${first}, sortKey: ${sortKey} ${cursor ? `, after: "${cursor}"` : ''}${filters ? `, filters: ${filters}` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              title
              availableForSale
              vendor
              productType
              createdAt
              tags
              ${queryAddon}
             variantYes: variantBySelectedOptions(selectedOptions: [
                { name: "Device", value: "${favPhone}" },
                { name: "Modèle", value: "${favPhone}" },
                { name: "Case Type", value: "Impact (Gloss)"},
                { name: "Type d'étuis", value: "Impact (Lustré)"},
                { name: "Bumper Color", value: "Clear"},
                { name: "MagSafe Ring", value: "Yes"}
              ], ignoreUnknownOptions: true) {
                id
                image {
                  url
                  altText
                }
                price {
                  amount
                  currencyCode
                }
                  selectedOptions {
                  name
                  value
                }
              }
             variantNo: variantBySelectedOptions(selectedOptions: [
                { name: "Device", value: "${favPhone}" },
                { name: "Modèle", value: "${favPhone}" },
                { name: "Case Type", value: "Impact (Gloss)"},
                { name: "Type d'étuis", value: "Impact (Lustré)"},
                { name: "Bumper Color", value: "Clear"},
                { name: "MagSafe Ring", value: "No"}
              ], ignoreUnknownOptions: true) {
                id
                image {
                  url
                  altText
                }
                price {
                  amount
                  currencyCode
                }
                  selectedOptions {
                  name
                  value
                }
              }
              images(first: 3) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              featuredImage {
                url
                altText
                id
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `;
}

function buildDesignQuery(designValue: string, first = 12, cursor: string | null = null): string {
  const filters = `[
    { productMetafield: { namespace: "custom", key: "designs", value: "${designValue}" } }
  ]`;
  return buildQuery(first, cursor, filters);
}

// Query to fetch all products in the collection with their design metafield
function buildAllDesignsQuery(first = 250, cursor: string | null = null): string {
  const filters = `[]`;
  const queryAddon = `
    metafield(namespace: "custom", key: "designs") {
      value
    }
  `;
  return buildQuery(first, cursor, filters, null, queryAddon);
}

function slugifyDesignToCollectionHandle(designName: string): string {
  return String(designName || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Query to fetch products from a per-design automated collection.
function buildDesignCollectionQuery(designName: string, first = 24, cursor: string | null = null): string {
  const filters = `[]`;
  const designHandle = `design-${slugifyDesignToCollectionHandle(designName)}`;
  return buildQuery(first, cursor, filters, designHandle);
}

async function fetchAllDesignProducts(cursor: string | null = null, accumulated: DCFProduct[] = []): Promise<DCFProduct[]> {
  const query = buildAllDesignsQuery(250, cursor);
  //console.log(query);
  const response = await fetch(dcfState.shopUrl + '/api/2026-01/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': dcfState.accessToken
    },
    body: JSON.stringify({ query })
  });

  if (!response.ok) throw new Error('Network response was not ok');
  const data: DCFGraphQLResponse = await response.json();
  const products = data.data?.collection?.products;
  if (!products) return accumulated;

  const newAccumulated = accumulated.concat(products.edges.map(edge => edge.node));

  if (products.pageInfo?.hasNextPage && products.pageInfo.endCursor) {
    return fetchAllDesignProducts(products.pageInfo.endCursor, newAccumulated);
  }

  return newAccumulated;
}

// Fetch products from the all-products collection for a design, excluding current collection products
async function fetchAllProductsByDesignExcludingCurrent(designName: string, first = 24, cursor: string | null = null): Promise<DCFProductsConnection | null> {
  const query = buildDesignCollectionQuery(designName, first, cursor);
  //console.log(query);
  const response = await fetch(dcfState.shopUrl + '/api/2026-01/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': dcfState.accessToken
    },
    body: JSON.stringify({ query })
  });

  if (!response.ok) throw new Error('Network response was not ok');
  const data: DCFGraphQLResponse = await response.json();
  const products = data.data?.collection?.products;
  if (!products) return null;
  return products;
}

function getExistingProductHandlesFromList(ul: Element | null): Set<string> {
  const handles = new Set<string>();
  if (!ul) return handles;

  ul.querySelectorAll('a[href*="/products/"]').forEach(a => {
    const href = a.getAttribute('href') || '';
    const match = href.match(/\/products\/([^?\/#]+)(?:[?#]|$)/);
    if (match && match[1]) handles.add(match[1]);
  });

  return handles;
}

async function fetchNextNonExcludedProductsByDesign(designName: string, ul: Element | null, first = 16, cursor: string | null = null, maxPages = 10): Promise<DCFProductsConnection | null> {
  let currentCursor = cursor;
  let pagesFetched = 0;
  const existingHandles = getExistingProductHandlesFromList(ul);

  while (pagesFetched < maxPages) {
    pagesFetched += 1;
    const products = await fetchAllProductsByDesignExcludingCurrent(designName, first, currentCursor);
    if (!products || !products.edges) return null;
    const filteredEdges = products.edges.filter(({ node }) => {
      if (!node || !node.handle) return false;
      if (existingHandles.has(node.handle)) return false;
      if (currentCollectionProductHandles.has(node.handle)) return false;
      return true;
    });

    if (filteredEdges.length > 0) {
      products.edges = filteredEdges;
      return products;
    }

    const pageInfo = products.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) {
      return null;
    }

    currentCursor = pageInfo.endCursor;
  }

  return null;
}


const CARD_IMAGE_WIDTHS = [200, 250, 300, 350, 500, 600, 700];
const CARD_IMAGE_SIZES = '(min-width: 1200px) 300px, (min-width: 990px) 250px, (min-width: 750px) 350px, (min-width: 500px) 250px, 200px';

function buildResponsiveCardImg(url: string, alt: string | null | undefined): string {
  const srcset = CARD_IMAGE_WIDTHS.map((w) => `${url}&width=${w} ${w}w`).join(', ');
  return `<img src="${url}&width=300" srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}" alt="${alt}" loading="lazy">`;
}

function  buildDCFImage(currentProduct: DCFProduct): string {
  let imageurl = currentProduct.featuredImage ? currentProduct.featuredImage.url : '';
  let imagealt = currentProduct.featuredImage ? currentProduct.featuredImage.altText : '';
  if (currentProduct.productType == "Clear Case" || currentProduct.productType == "Phone Case") {
    const selectedVariant = currentProduct.variantYes || currentProduct.variantNo || currentProduct.selectedOrFirstAvailableVariant;
    const variantImage = selectedVariant?.image;
    if (variantImage?.url) {
      imageurl = variantImage.url;
      imagealt = variantImage.altText || imagealt;
    } else {
      // Variant has no dedicated image assigned in Shopify admin.
      // Try to find a product image whose alt text contains the selected device name.
      const deviceOption = selectedVariant?.selectedOptions?.find(
        (o) => o.name === 'Device' || o.name === 'Modèle'
      );
      const deviceName = deviceOption?.value;
      if (deviceName) {
        const matchingImage = currentProduct.images?.edges.find(
          (e) => e.node.altText?.toLowerCase().includes(deviceName.toLowerCase())
        );
        if (matchingImage) {
          imageurl = matchingImage.node.url;
          imagealt = matchingImage.node.altText || imagealt;
        }
      }
    }
  }

  let hoveraddon = '';
  if (currentProduct.productType == 'Folio Case' && currentProduct.featuredImage) {
    if (currentProduct.featuredImage) {
      const hoverURL = currentProduct.featuredImage.url;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverURL)}
        </div>`;
    }
  } else if (currentProduct.productType == 'iPad Folio Case') {
    const image = currentProduct.images?.edges[2]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }

  } else if (currentProduct.productType == 'Airpod Cases') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  } else if (currentProduct.productType == 'ChargePods') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  }
  let imageSection = '';
  if (imageurl == '') {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect --missing-image">
          <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
        </div>
    </div>`;
  } else {
    imageSection = `<div class="card__media">
      <div class="media media--transparent media--hover-effect">
        ${buildResponsiveCardImg(imageurl, imagealt)}
      </div>
      ${hoveraddon}
  </div>`;
  }


  return imageSection;
}

// Fonction pour construire le HTML d'un produit
function buildProductHtml(product: DCFProduct, order: number): string {
  const { id, title, handle, availableForSale, selectedOrFirstAvailableVariant, variantYes, variantNo, tags, vendor, productType, priceRange } = product;

  const isEarlyAccess = vendor === 'Early Access';

  const selectedVariant = variantYes || variantNo || selectedOrFirstAvailableVariant;
  const variantId = selectedVariant ? selectedVariant.id.replace("gid://shopify/ProductVariant/", "") : null;
  let productUrl = handle;
  if(variantId) {
      productUrl = productUrl + '?variant=' + variantId;
  }

  const new_title = title;

  let preprice = dcfState.currentLanguage == 'FR' ? 'À partir de' : 'From';
  if(priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
    preprice = '';
  } else if (productType.indexOf('iPad') > -1) {
    preprice = '';
  }

  const urlPrefix = dcfState.currentLanguage == 'FR' ? '/fr' : '';

  let forceBadge = '';
  let finalSaleBadge = '';

  if(document.body.dataset.forceBadge == 'true' && document.body.dataset.forceBadgeType?.includes(productType)) {
    forceBadge = `<span class="badge badge--${document.body.dataset.forceBadgeColor}">${document.body.dataset.forceBadgeText}</span>`;
  }

  let oosBadge = '';
  let lowStockBadge = '';
  let newBadge = '';
  let colorLabel = '';
  const newTag = tags.filter(tag => {
    return tag == 'New';
  });
  const venteFinalTag = tags.filter(tag => {
    return tag == 'final_sale';
  });

  if(!availableForSale) {
    oosBadge = dcfState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
  } else {
    const lowTag = tags.filter(tag => {
      return tag == 'low-stock';
    });
    if(lowTag.length > 0) {
      lowStockBadge = dcfState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>':'<span class="badge">Low Stock</span>';
    }
  }

  if(newTag.length > 0) {
    newBadge = dcfState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
  }
  if(venteFinalTag.length > 0) {
    finalSaleBadge = dcfState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>':'<span class="badge">Final Sale</span>';
  }

  const clearCondition = productType.indexOf('Clear Case') > -1 && productType.indexOf('iPad') == -1;

  let price: number | string = clearCondition ? 34.99 : priceRange.minVariantPrice.amount;

  if (productType.indexOf('ChargePods') > -1) {
    price = 79.99;
    preprice = "";
  }

  const enPriceTag = dcfState.currentLanguage == 'EN' ? '$' : '';
  const frPriceTag = dcfState.currentLanguage == 'FR' ? '$' : '';
  const currencyTag = dcfState.currentMarket === 'CA' ? ' CAD' : ' USD';
  const priceFormatted = parseFloat(String(price)).toFixed(2);

  let contentHTML = `<div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      ${preprice} ${enPriceTag}${priceFormatted}${frPriceTag}${currencyTag}
                    </div>
                  </div>
                </div>
                ${colorLabel}`;

  if(isEarlyAccess) {
    contentHTML = `<span class="card__badge badge-early-access">${dcfState.currentLanguage == 'EN' ? 'Coming Soon' : 'Bientôt disponible'}</span>`;
  }

  return `
    <li class="grid__item" style="order: ${order}" data-order="${order}">
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <a href="${urlPrefix}/products/${productUrl}">
          <span class="visually-hidden">${new_title}</span>
        </a>
        <div class="card card--standard card--media">
          <div class="card__inner">
            ${buildDCFImage(product)}
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${new_title}
                </a>
              </h3>
              <div class="card-information">
                ${contentHTML}
              </div>
            </div>
          </div>
          <div class="card__badge top left">
            ${newBadge}
            ${lowStockBadge}
            ${oosBadge}
            ${forceBadge}
            ${finalSaleBadge}
          </div>
        </div>
      </div>
    </li>
  `;
}

// Gestionnaire de filtres
class FilterManager {
  constructor() {
    this.initializePhoneSelector();
  }

  initializePhoneSelector(): void {
    const phoneSelector = document.querySelector(dcfSelectors.filters.phoneDevice) as HTMLSelectElement | null;
    if (!phoneSelector) return;
    if(phoneSelector.options.length > 0) {
      const rawCurrentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
      let currentFavPhone = 'iPhone 17';
      try { currentFavPhone = decodeURIComponent(rawCurrentFavPhone) || 'iPhone 17'; } catch { currentFavPhone = rawCurrentFavPhone; }
      phoneSelector.value = currentFavPhone;

      new Choices(phoneSelector, {shouldSort: false, shouldSortItems: false});
      this.setupSelectorEvents(phoneSelector);
    }
  }

  setupSelectorEvents(selector: HTMLSelectElement): void {
    selector.addEventListener('change', () => {
      CookieManager.setCookie('favPhone', selector.value);
      loadAllDesignSections();
    });
  }
}

function loadAllDesignSections(): void {
  fetchAllDesignProducts()
    .then(products => {
      currentCollectionProductHandles.clear();
      products.forEach(p => {
        if (p?.handle) currentCollectionProductHandles.add(p.handle);
      });

      const byDesign = new Map<string, DCFProduct[]>();

      products.forEach(product => {
        const rawValue = product.metafield?.value;
        if (!rawValue) return;

        let designKey: string | undefined;
        try {
          const parsed = JSON.parse(rawValue);
          designKey = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch {
          designKey = rawValue;
        }

        if (!designKey) return;
        if (!byDesign.has(designKey)) byDesign.set(designKey, []);
        byDesign.get(designKey)!.push(product);
      });

      const designArticles = document.querySelectorAll('article[data-design]');
      designArticles.forEach(article => {
        const designKey = (article as HTMLElement).dataset.design;
        if (!designKey) return;
        const productsForDesign = byDesign.get(designKey) || [];
        const ul = article.querySelector('ul[data-design]');
        if (!ul) return;

        ul.querySelectorAll('li.grid__item:not(.grid__item__promo)').forEach(li => li.remove());

        let order = 0;
        let html = '';
        productsForDesign.forEach(product => {
          order += 1;
          html += buildProductHtml(product, order);
        });

        ul.insertAdjacentHTML('beforeend', html);

        const loadMoreRow = article.querySelector('.load-more-row');
        if (loadMoreRow) {
          loadMoreRow.classList.add('--hidden');
        }
      });
    })
    .catch(error => {
      console.error('Error during design products load:', error);
    });
}

// NOTE: fetchCollectionProductsByDesign, ProductDesignManager, and LazyLoader appear to be
// dead code — they reference undefined variables (Manager, order) and functions
// (fetchCollectionProductsByProductType). They are not called from the DOMContentLoaded handler.
// Preserved as-is for reference but not migrated to avoid introducing type errors for broken code.

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  if (isSafari(15.6)) {
    document.querySelector(dcfSelectors.mainContent)?.classList.add('--safari');
  }

  new FilterManager();
  loadAllDesignSections();

  // Handle "See all" buttons for each design section
  const seeAllButtons = document.querySelectorAll('button[data-design]');
  seeAllButtons.forEach(button => {
    button.addEventListener('click', async () => {
      const designName = (button as HTMLElement).dataset.design;
      if (!designName) return;

      const article = document.querySelector(`article[data-design="${designName}"]`);
      if (!article) return;

      const ul = article.querySelector('ul[data-design]');
      if (!ul) return;

      let lastOrder = 0;
      const existingItems = ul.querySelectorAll('li.grid__item:not(.grid__item__promo)');
      if (existingItems.length > 0) {
        const lastItem = existingItems[existingItems.length - 1] as HTMLElement;
        if (lastItem.dataset.order) {
          lastOrder = parseInt(lastItem.dataset.order, 10) || 0;
        }
      }

      const lastExistingItem = existingItems.length > 0 ? existingItems[existingItems.length - 1] : null;
      (lastExistingItem || ul).scrollIntoView({ behavior: 'smooth', block: 'end' });

      let products: DCFProductsConnection | null;
      try {
        products = await fetchNextNonExcludedProductsByDesign(designName, ul, 16, null, 10);
      } catch (e) {
        console.error('Error fetching additional products for design:', designName, e);
        return;
      }

      if (!products || !products.edges || !products.edges.length) {
        return;
      }

      let order = lastOrder;
      let html = '';
      products.edges.forEach(({ node }) => {
        order += 1;
        html += buildProductHtml(node, order);
      });

      ul.insertAdjacentHTML('beforeend', html);

      const pageInfo = products.pageInfo;
      const loadMoreRow = article.querySelector('.load-more-row');
      const loadMoreButton = loadMoreRow?.querySelector('button[data-load-more]') as HTMLElement | null;

      if (pageInfo && pageInfo.hasNextPage && pageInfo.endCursor && loadMoreRow && loadMoreButton) {
        (article as HTMLElement).dataset.extraCursor = pageInfo.endCursor;
        (article as HTMLElement).dataset.extraHasNext = 'true';
        loadMoreRow.classList.remove('--hidden');
        loadMoreButton.classList.remove('--hidden');

        if (!loadMoreButton.dataset.extraBound) {
          loadMoreButton.dataset.extraBound = 'true';
          loadMoreButton.addEventListener('click', async (event) => {
            event.preventDefault();

            if ((article as HTMLElement).dataset.extraHasNext !== 'true') return;
            const cursor = (article as HTMLElement).dataset.extraCursor;
            if (!cursor) return;

            let moreLastOrder = 0;
            const currentItems = ul.querySelectorAll('li.grid__item:not(.grid__item__promo)');
            if (currentItems.length > 0) {
              const last = currentItems[currentItems.length - 1] as HTMLElement;
              if (last.dataset.order) {
                moreLastOrder = parseInt(last.dataset.order, 10) || 0;
              }
            }

            let moreProducts: DCFProductsConnection | null;
            try {
              moreProducts = await fetchNextNonExcludedProductsByDesign(designName, ul, 16, cursor, 10);
            } catch (e) {
              console.error('Error loading more products for design:', designName, e);
              return;
            }

            if (!moreProducts || !moreProducts.edges || !moreProducts.edges.length) {
              (article as HTMLElement).dataset.extraHasNext = 'false';
              loadMoreRow.classList.add('--hidden');
              return;
            }

            let moreOrder = moreLastOrder;
            let moreHtml = '';
            moreProducts.edges.forEach(({ node }) => {
              moreOrder += 1;
              moreHtml += buildProductHtml(node, moreOrder);
            });

            ul.insertAdjacentHTML('beforeend', moreHtml);

            const morePageInfo = moreProducts.pageInfo;
            if (morePageInfo && morePageInfo.hasNextPage && morePageInfo.endCursor) {
              (article as HTMLElement).dataset.extraCursor = morePageInfo.endCursor;
              (article as HTMLElement).dataset.extraHasNext = 'true';
            } else {
              (article as HTMLElement).dataset.extraHasNext = 'false';
              loadMoreRow.classList.add('--hidden');
            }
          });
        }
      } else if (loadMoreRow) {
        (article as HTMLElement).dataset.extraHasNext = 'false';
        loadMoreRow.classList.add('--hidden');
      }
    });
  });
});
